import { DatastoresTracking } from "@/lib/tracking/datastores";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Checkbox,
  Label,
  Tooltip,
  Typography,
  Badge,
  Button,
  Card,
  Stack,
  Spinner,
} from "@altimateai/lego";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";
import { IconRefresh, IconPlayerPlay, IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import { getIconByDatastoreType } from "../DatastoreImage";
import {
  testDatastoreConnection,
  syncDatastore,
  toggleDatastoreSyncSchedule,
} from "@/helpers/apis";
import { Datastore } from "../../types";
import { TestState, API_RESULT_TIMEOUT } from "../../constants";

interface DatastoreCardProps {
  datastore: Datastore;
  syncOpAllowed: boolean;
}

const DatastoreCard = ({ datastore, syncOpAllowed }: DatastoreCardProps): JSX.Element => {
  const [isChecked, setIsChecked] = useState(datastore?.auto_sync || false);
  const [testState, setTestState] = useState<TestState>(TestState.Uninitialized);
  const [showSyncing, setShowSyncing] = useState(false);
  const isTestDisabled = !datastore?.can_sync;

  const { mutate: runTestConnection, isPending: isTestLoading } = useMutation({
    mutationFn: () => testDatastoreConnection(datastore.id),
    onSuccess: response => {
      if (response?.ok) {
        setTestState(TestState.Success);
        setTimeout(() => {
          setTestState(TestState.Uninitialized);
        }, API_RESULT_TIMEOUT.DATASTORE);
      } else {
        setTestState(TestState.Fail);
        setTimeout(() => {
          setTestState(TestState.Uninitialized);
        }, API_RESULT_TIMEOUT.DATASTORE);
      }
    },
    onError: () => {
      setTestState(TestState.Fail);
      setTimeout(() => {
        setTestState(TestState.Uninitialized);
      }, API_RESULT_TIMEOUT.DATASTORE);
    },
  });
  const { mutate: runSyncDatastore } = useMutation({
    mutationFn: () => syncDatastore(datastore.id),
    onMutate: () => DatastoresTracking.syncTriggered(datastore.type),
    onSuccess: () => setShowSyncing(false),
    onError: () => setShowSyncing(false),
  });
  const { mutate: runToggleSchedule } = useMutation({
    mutationFn: () => toggleDatastoreSyncSchedule(datastore.id),
    onMutate: () => DatastoresTracking.autoSyncToggled(datastore.type, !isChecked),
    onSuccess: res => {
      if (res?.ok) setIsChecked(!isChecked);
    },
  });
  const handleTestConnection = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isTestDisabled) runTestConnection();
  };
  const handleSync = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowSyncing(true);
    runSyncDatastore();
  };

  const getTestIcon = () => {
    if (isTestLoading) return <Spinner size="sm" />;
    if (testState === TestState.Success)
      return <IconCircleCheck size={20} className="al-text-[#01cd8c]" />;
    if (testState === TestState.Fail)
      return <IconCircleX size={20} className="al-text-[#ee140f]" />;
    return <IconPlayerPlay size={20} className={isTestDisabled ? "al-text-gray-400" : ""} />;
  };

  return (
    <Link to={`/settings/datastores/${datastore.id}`} className="al-block">
      <Card className="al-cursor-pointer hover:al-shadow-md al-transition-shadow">
        <Stack direction="row" justify="between" align="center" className="al-mb-2 al-px-5 al-pt-4">
          <Stack direction="row" justify="between" align="center">
            {getIconByDatastoreType({
              datastoreType: datastore.type,
              className: "al-rounded al-w-14 al-h-14",
            })}
            <div className="al-ms-2">
              <Typography variant="section-heading">{datastore.name}</Typography>
              <Typography size="sm" className="al-text-brand">
                {datastore.type}
              </Typography>
            </div>
          </Stack>

          <Stack
            direction="column"
            align="end"
            className="al-w-[50px]"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {datastore.can_sync && (
              <Stack direction="row" align="center" justify="between">
                <Label
                  className="al-pointer-events-none al-text-[10px] al-leading-[15px] al-text-[#082247] al-whitespace-nowrap"
                  htmlFor={"flexSwitchCheckDefault" + datastore.id}
                >
                  Enable Sync
                </Label>
                <Tooltip title={!syncOpAllowed ? "Insufficient permissions for enabling sync" : ""}>
                  <Checkbox
                    role="switch"
                    id={"flexSwitchCheckDefault" + datastore.id}
                    checked={isChecked}
                    onCheckedChange={() => runToggleSchedule()}
                    disabled={!syncOpAllowed}
                  />
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack
          direction="row"
          justify="between"
          align="center"
          className="al-mb-2 al-text-gray al-px-5"
        >
          {datastore?.description}
        </Stack>

        <Stack
          direction="row"
          align="center"
          className="al-py-1 al-px-[14px] al-pl-5 al-rounded-b-[20px] al-bg-[#f5f6f7] al-row-[3/4]"
        >
          <Stack direction="row" justify="between" align="center" className="al-mt-1 al-w-full">
            {testState === TestState.Success && (
              <Typography size="sm" className="al-text-[#01cd8c]">
                Success
              </Typography>
            )}
            {testState === TestState.Fail && (
              <Typography size="sm" className="al-text-[#ee140f]">
                Failed, Try Again
              </Typography>
            )}
            {testState === TestState.Uninitialized &&
              (datastore?.last_synced_at ? (
                <Stack direction="row" align="center" className="al-flex-nowrap">
                  <Typography size="xs">
                    <span className="al-text-gray">Last Sync:</span>{" "}
                    {dayjs(datastore?.last_synced_at).format("h:mm A, D MMMM YYYY")}
                  </Typography>
                  {datastore?.last_sync_status === "success" ? (
                    <Badge variant="success" key={"success"}>
                      Success
                    </Badge>
                  ) : datastore?.last_sync_status === "failed" ? (
                    <Badge variant="destructive">Failed</Badge>
                  ) : (
                    <Badge variant="warning">Running</Badge>
                  )}
                </Stack>
              ) : (
                <div></div>
              ))}

            <Stack direction="row" gap="sm">
              {datastore?.can_sync && syncOpAllowed ? (
                <Button
                  variant="ghost"
                  size="xs"
                  className="!al-bg-white al-me-2"
                  onClick={handleSync}
                  aria-label="Sync datastore now"
                >
                  {showSyncing ? (
                    <>
                      <IconRefresh size={20} className="al-animate-spin" />
                      <Typography size="sm">Syncing...</Typography>
                    </>
                  ) : (
                    <>
                      <IconRefresh size={20} />
                      <Typography size="sm">Sync Now</Typography>
                    </>
                  )}
                </Button>
              ) : (
                <Tooltip
                  title={
                    !syncOpAllowed
                      ? "Insufficient permissions for performing sync"
                      : "Credentials are not added or synced to SaaS UI"
                  }
                >
                  <Button
                    variant="ghost"
                    size="xs"
                    className="!al-bg-white al-me-2"
                    disabled
                    aria-label="Sync datastore now"
                  >
                    <IconRefresh size={20} className="al-text-gray-400" />
                    <Typography size="sm">Sync Now</Typography>
                  </Button>
                </Tooltip>
              )}

              <Button
                variant="ghost"
                size="xs"
                className={cn("!al-bg-white", {
                  "al-pointer-events-none": isTestDisabled,
                })}
                title={!isTestDisabled ? "Test Connection" : "Sync not enabled"}
                onClick={handleTestConnection}
                disabled={isTestDisabled}
                aria-label="Test datastore connection"
              >
                {getTestIcon()}
                <Typography size="sm">Test</Typography>
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </Link>
  );
};

export default DatastoreCard;
